/**
 * /inspections/:id — reporte imprimible.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Pencil, Printer, Trash2, Wrench } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { StatusPill } from '@/components/ui/status-pill'
import { ApiError, createWorkOrder, deleteInspection, getInspection, patchInspection } from '@/lib/api'
import { Field } from '@/components/ui/field'

export function InspectionDetailPage() {
  const { inspectionId } = useParams({ from: '/inspections/$inspectionId' })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['inspection', inspectionId],
    queryFn: () => getInspection(inspectionId),
  })

  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const removeInspection = useMutation({
    mutationFn: () => deleteInspection(inspectionId),
    onSuccess: async () => {
      // La lectura de odómetro que generó esta inspección se va con ella y el
      // servidor recalcula el del vehículo, así que hay que refrescar también
      // la flota.
      await qc.invalidateQueries({ queryKey: ['inspections'] })
      await qc.invalidateQueries({ queryKey: ['vehicles'] })
      void navigate({ to: '/inspections' })
    },
    onError: (err) => {
      setConfirmDelete(false)
      setError(err instanceof ApiError ? err.message : 'No se pudo eliminar.')
    },
  })

  const createWo = useMutation({
    mutationFn: createWorkOrder,
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['work-orders'] })
      void navigate({
        to: '/work-orders/$workOrderId',
        params: { workOrderId: res.workOrder.id },
      })
    },
  })

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>
  if (query.isError || !query.data) {
    return (
      <Card>
        <EmptyState
          title="Inspección no encontrada"
          description={(query.error as Error | null)?.message}
          action={
            <Link to="/inspections" className="text-sm font-medium text-primary">
              Volver
            </Link>
          }
        />
      </Card>
    )
  }

  const insp = query.data.inspection
  const items = insp.items ?? []
  const repairItems = items.filter((i) => i.condition === 'Repair')

  return (
    <>
      <div className="no-print mb-3 flex items-center justify-between gap-2">
        <Link
          to="/inspections"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Inspections
        </Link>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setEditing((v) => !v)
              setError(null)
            }}
          >
            <Pencil className="size-4" aria-hidden />
            {editing ? 'Cerrar' : 'Editar'}
          </Button>
          {confirmDelete ? (
            <>
              <Button
                type="button"
                variant="danger"
                size="sm"
                loading={removeInspection.isPending}
                onClick={() => removeInspection.mutate()}
              >
                Confirmar
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(false)}
              >
                No
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="size-4" aria-hidden />
              Eliminar
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="size-4" aria-hidden />
            Print
          </Button>
        </div>
      </div>

      {error && (
        <p role="alert" className="no-print mb-3 text-sm font-medium text-danger">
          {error}
        </p>
      )}

      {editing && <InspectionEditor insp={insp} onDone={() => setEditing(false)} onError={setError} />}

      <PageHeader
        title={`Unit ${insp.unitNumber}`}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span>{insp.inspectionType}</span>
            <StatusPill status={insp.overallStatus} />
            <span className="text-muted-foreground">
              {new Date(insp.inspectionDatetime).toLocaleString()}
            </span>
          </span>
        }
      />

      {repairItems.length > 0 && (
        <Card className="no-print mb-4 border-danger/40">
          <CardBody className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              <Wrench className="mr-2 inline size-4" aria-hidden />
              {repairItems.length} ítem(s) en <strong>Repair</strong> — puedes abrir una orden.
            </p>
            <Button
              type="button"
              size="sm"
              loading={createWo.isPending}
              onClick={() =>
                createWo.mutate({
                  vehicleId: insp.vehicleId,
                  title: `Hallazgos inspección ${insp.inspectionType}`,
                  description: repairItems
                    .map((i) => `${i.category} · ${i.label}`)
                    .join('\n'),
                  priority: 'High',
                  status: 'Reported',
                  items: repairItems.map((i) => ({
                    itemType: 'Other',
                    description: `${i.category} · ${i.label}`,
                    quantity: 1,
                    unitCost: 0,
                  })),
                })
              }
            >
              Crear work order
            </Button>
          </CardBody>
        </Card>
      )}

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Resumen" />
          <CardBody className="space-y-2 text-sm">
            <Row label="Inspector" value={insp.inspectorName ?? '—'} />
            <Row
              label="Odómetro"
              value={insp.odometer != null ? insp.odometer.toLocaleString() : '—'}
            />
            <Row label="Ubicación" value={insp.locationText ?? '—'} />
            <Row label="Notas" value={insp.notes ?? '—'} />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Checklist" />
        <CardBody className="divide-y divide-border p-0">
          {items.map((it) => (
            <div
              key={it.id}
              className="flex min-h-[52px] items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {it.category} · {it.label}
                </p>
                {it.notes && <p className="text-xs text-muted-foreground">{it.notes}</p>}
              </div>
              <StatusPill status={it.condition} />
            </div>
          ))}
        </CardBody>
      </Card>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium break-all">{value}</span>
    </div>
  )
}

const CONDITIONS = ['Good', 'Attention', 'Repair', 'Not Applicable'] as const

/**
 * Corrección de una inspección ya guardada.
 *
 * Los ítems se mandan SIEMPRE completos: el endpoint sustituye la lista entera
 * (ver api/inspections/[id].ts). Enviar solo los cambiados obligaría a
 * distinguir "sin tocar" de "borrado", y ahí es donde se pierden ítems sin que
 * nadie se entere.
 *
 * Cambiar la condición de un ítem recalcula el estado global por trigger
 * (recalc_inspection_status): pasar el último 'Repair' a 'Good' devuelve la
 * inspección a "Passed" sola.
 */
function InspectionEditor({
  insp,
  onDone,
  onError,
}: {
  insp: {
    id: string
    inspectionType: string
    odometer: number | null
    locationText: string | null
    notes: string | null
    items?: {
      id: string
      category: string
      label: string
      condition: string
      notes: string | null
      measurementUnit: string | null
    }[]
  }
  onDone: () => void
  onError: (message: string | null) => void
}) {
  const qc = useQueryClient()
  const [type, setType] = useState(insp.inspectionType)
  const [odometer, setOdometer] = useState(insp.odometer != null ? String(insp.odometer) : '')
  const [location, setLocation] = useState(insp.locationText ?? '')
  const [notes, setNotes] = useState(insp.notes ?? '')
  const [items, setItems] = useState(insp.items ?? [])

  const save = useMutation({
    mutationFn: () =>
      patchInspection(insp.id, {
        inspectionType: type,
        odometer: odometer ? Number(odometer) : null,
        locationText: location || null,
        notes: notes || null,
        items: items.map((it, i) => ({
          category: it.category,
          label: it.label,
          condition: it.condition,
          notes: it.notes,
          measurementUnit: it.measurementUnit,
          sortOrder: (i + 1) * 10,
        })),
      }),
    onSuccess: async () => {
      onError(null)
      await qc.invalidateQueries({ queryKey: ['inspection', insp.id] })
      await qc.invalidateQueries({ queryKey: ['inspections'] })
      // El odómetro de la inspección se copia a odometer_history y al vehículo.
      await qc.invalidateQueries({ queryKey: ['vehicles'] })
      onDone()
    },
    onError: (err) => onError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  return (
    <Card className="no-print mb-4">
      <CardHeader title="Editar inspección" />
      <CardBody className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium">Tipo</span>
            <select
              className="h-11 rounded-lg border border-input bg-card px-3"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {['Pre-Trip', 'Post-Trip', 'Daily', 'Weekly', 'Monthly', 'Safety', 'Maintenance', 'Custom'].map(
                (t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ),
              )}
            </select>
          </label>
          <Field
            label="Odómetro"
            type="number"
            inputMode="decimal"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
          />
        </div>
        <Field label="Ubicación" value={location} onChange={(e) => setLocation(e.target.value)} />
        <Field label="Notas" value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-2 border-t border-border pt-2">
              <span className="min-w-0 flex-1 text-sm">
                {it.category} · {it.label}
              </span>
              <select
                className="h-11 rounded-lg border border-input bg-card px-2 text-sm"
                value={it.condition}
                aria-label={`Condición de ${it.category} ${it.label}`}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((p, i) => (i === idx ? { ...p, condition: e.target.value } : p)),
                  )
                }
              >
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <Button type="button" className="flex-1" loading={save.isPending} onClick={() => save.mutate()}>
            Guardar cambios
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}
